import { createSelector } from '@ngrx/store';

import { MakeRef } from '../types';
import { createActionsWithReducer } from '../utils';
import { EntityCollectionActions } from './actions';
import { createEntityAdapter } from './adapter';
import {
  EntityCollectionStoreOptions,
  EntityCollectionStoreCustomActions,
  GenericEntity,
  EntityState,
  GenericIdSelector,
  GenericMetaOptions,
  ID,
  DefaultIdSelector,
} from './types';

type IdSelectorOrDefault<
  T extends GenericIdSelector<any> | undefined,
  U extends GenericEntity,
> = T extends GenericIdSelector<any> ? T : DefaultIdSelector<U>;

// eslint-disable-next-line max-lines-per-function
export function createEntityCollectionStore<
  Entity extends GenericEntity,
  MetaOptions extends GenericMetaOptions = GenericMetaOptions,
>() {
  // eslint-disable-next-line max-lines-per-function
  return <StoreOptions extends EntityCollectionStoreOptions<Entity, MetaOptions>>(
    namespace: string,
    options?: StoreOptions,
  ) => {
    const ns = `entityCollection/${namespace}`;

    const baseSelector = createSelector(
      (root: any) => <EntityState<Entity>>root[ns],
      state => state,
    );
    // @ts-ignore
    const idSelector: IdSelectorOrDefault<StoreOptions['idSelector'], Entity> =
      options?.idSelector || ((entity: { id: ID }) => entity.id);

    const makeRef: MakeRef<Entity> = (mapFrom, mapTo, source) => ({ mapFrom, mapTo, source });
    const {
      adapter,
      initialState: defaultInitState,
      selectors,
      defaultActionConfig,
    } = createEntityAdapter<
      Entity,
      IdSelectorOrDefault<StoreOptions['idSelector'], Entity>,
      MetaOptions
    >(baseSelector, ns, idSelector, options?.references?.(makeRef));
    const initialState = options?.initialState || defaultInitState;
    const mergedActions = <
      EntityCollectionActions<
        Entity,
        IdSelectorOrDefault<StoreOptions['idSelector'], Entity>,
        MetaOptions
      > &
        EntityCollectionStoreCustomActions<StoreOptions>
    >{
      ...defaultActionConfig,
      ...(options?.customActions?.(adapter) || {}),
    };

    const { actions, reducer } = createActionsWithReducer(namespace, initialState, mergedActions);
    return {
      namespace: ns,
      selectors,
      actions,
      reducer,
      initialState,
    };
  };
}
